import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Coins } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FIELDS = [
  ["opening_cash_on_hand", "Opening cash on hand (USD)", "Starting balance for the Project Funds cash summary"],
  ["inss_employee_pct", "INSS employee %", "Segurança Social — deducted from the employee (default 4)"],
  ["inss_employer_pct", "INSS employer %", "Paid by KINOS on top of gross (default 6)"],
  ["wht_pct", "Withholding tax %", "Applied to the part of gross above the threshold (0 = off)"],
  ["wht_threshold", "Tax-free threshold (USD / month)", "Timor-Leste resident default 500"],
];

/** Settings → Finance & Payroll: opening cash + INSS / withholding-tax rates. */
export function PayrollSettingsSection({ isAdmin }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["reminder-settings"], queryFn: api.getReminderSettings });
  const [form, setForm] = useState({});
  useEffect(() => {
    if (data) setForm(Object.fromEntries(FIELDS.map(([k]) => [k, String(data[k] ?? "")])));
  }, [data]);
  const saveMut = useMutation({
    mutationFn: () => api.updateReminderSettings(Object.fromEntries(FIELDS.map(([k]) => [k, Number(form[k] || 0)]))),
    onSuccess: () => { toast.success("Finance & payroll settings saved"); qc.invalidateQueries({ queryKey: ["reminder-settings"] }); qc.invalidateQueries({ queryKey: ["fund-cash"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  return (
    <section className="border border-border rounded-md bg-white p-5 mb-6" data-testid="payroll-settings-section">
      <h2 className="font-display font-semibold text-base flex items-center gap-2"><Coins className="size-4 text-primary" /> Finance &amp; payroll rates</h2>
      <p className="text-xs text-muted-foreground mt-1 mb-4">Used by Project Funds (cash on hand) and every new payroll run. Existing payslips keep the rates they were generated with.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {FIELDS.map(([k, l, hint]) => (
          <div key={k}>
            <Label className="overline">{l}</Label>
            <Input data-testid={`payroll-setting-${k}`} type="number" min="0" step="0.01" value={form[k] ?? ""} disabled={!isAdmin}
                   onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))} />
            <p className="text-[10px] text-muted-foreground mt-1">{hint}</p>
          </div>
        ))}
      </div>
      {isAdmin && <Button className="mt-3" data-testid="payroll-settings-save" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>{saveMut.isPending ? "Saving…" : "Save rates"}</Button>}
    </section>
  );
}
